import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../hooks/useAuth";
import "./admin.css";

const MODULES = [
  { id: "m0", title: "Intro", section: "START", position: 0, order: 1 },
  { id: "m1", title: "Multi Handed", section: "BELIEVE PHILOSOPHY", position: 2, order: 2 },
  { id: "m2", title: "10% / 3 Rules / 3 Goals", section: "BELIEVE PHILOSOPHY", position: 3, order: 3 },
  { id: "m3", title: "Self Improvement", section: "BELIEVE PHILOSOPHY", position: 4, order: 4 },
  { id: "m4", title: "Observe 4 Recruits", section: "FOLLOW SYSTEM", position: 6, order: 5 },
  { id: "m5", title: "Observe 4 Clients", section: "FOLLOW SYSTEM", position: 7, order: 6 },
  { id: "m6", title: "Get License", section: "FOLLOW SYSTEM", position: 8, order: 7 },
  { id: "m7", title: "1 Direct Registration", section: "FOLLOW SYSTEM", position: 9, order: 8 },
  { id: "m8", title: "9 Recruits", section: "BUILD OUTLET", position: 11, order: 9 },
  { id: "m9", title: "45,000 Personal Points", section: "BUILD OUTLET", position: 12, order: 10 },
  { id: "m10", title: "3 Licenses", section: "BUILD OUTLET", position: 13, order: 11 },
  { id: "m11", title: "15 Registrations on Base", section: "BUILD OUTLET", position: 14, order: 12 },
];

const SECTIONS = [
  { name: "START", icon: "🎯" },
  { name: "BELIEVE PHILOSOPHY", icon: "🛡️" },
  { name: "FOLLOW SYSTEM", icon: "👔" },
  { name: "BUILD OUTLET", icon: "⌚" },
];

export default function OnboardingVideoManager() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("free");
  const [videos, setVideos] = useState({ free: {}, paid: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [expandedSections, setExpandedSections] = useState({});
  const [editModal, setEditModal] = useState({ open: false, moduleId: null, tier: null });
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    loadVideos();
    // Expand all sections by default
    const defaultExpanded = {};
    SECTIONS.forEach(section => {
      defaultExpanded[section.name] = true;
    });
    setExpandedSections(defaultExpanded);
  }, []);

  const loadVideos = async () => {
    try {
      const docRef = doc(db, "config", "onboarding_videos");
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setVideos(docSnap.data());
      }
    } catch (error) {
      console.error("Error loading videos:", error);
      showMessage("Error loading videos: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, "config", "onboarding_videos");
      await setDoc(docRef, videos);
      showMessage("✅ Videos saved successfully!", "success");
    } catch (error) {
      console.error("Error saving videos:", error);
      showMessage("❌ Error saving: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const updateVideo = (tier, moduleId, field, value) => {
    setVideos(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        [moduleId]: {
          ...prev[tier][moduleId],
          [field]: value
        }
      }
    }));
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const getModulesBySection = (sectionName) => {
    return MODULES.filter(m => m.section === sectionName).sort((a, b) => a.order - b.order);
  };

  const validateAndConvertUrl = (url) => {
    if (!url || url.trim() === "") {
      return { isValid: true, convertedUrl: "", type: "empty", message: "No URL provided" };
    }

    try {
      // Firebase Storage URL
      if (url.includes("firebasestorage.googleapis.com")) {
        return { isValid: true, convertedUrl: url, type: "firebase", message: "🔥 Firebase Storage" };
      }

      // Direct video file
      if (url.match(/\.(mp4|webm|ogg)(\?|$)/i)) {
        return { isValid: true, convertedUrl: url, type: "direct", message: "🎥 Direct Video" };
      }

      // YouTube URLs
      if (url.includes("youtube.com") || url.includes("youtu.be")) {
        let videoId;
        if (url.includes("youtu.be/")) {
          videoId = url.split("youtu.be/")[1].split("?")[0];
        } else if (url.includes("watch?v=")) {
          videoId = url.split("watch?v=")[1].split("&")[0];
        }
        
        if (videoId) {
          const embedUrl = `https://www.youtube.com/embed/${videoId}`;
          return { isValid: true, convertedUrl: embedUrl, type: "youtube", message: "🔄 Auto-converted to YouTube embed" };
        }
      }

      // Already YouTube embed
      if (url.includes("youtube.com/embed/")) {
        return { isValid: true, convertedUrl: url, type: "youtube", message: "✅ YouTube Embed" };
      }

      // Vimeo URLs
      if (url.includes("vimeo.com")) {
        return { isValid: true, convertedUrl: url, type: "vimeo", message: "✅ Vimeo" };
      }

      // Generic HTTP/HTTPS URL
      if (url.startsWith("http://") || url.startsWith("https://")) {
        return { isValid: true, convertedUrl: url, type: "external", message: "🔗 External URL" };
      }

      return { isValid: false, convertedUrl: url, type: "unknown", message: "⚠️ Unknown URL format" };
    } catch (error) {
      return { isValid: false, convertedUrl: url, type: "error", message: "❌ Invalid URL" };
    }
  };

  const handleUrlBlur = (tier, moduleId, url) => {
    const result = validateAndConvertUrl(url);
    if (result.convertedUrl !== url) {
      updateVideo(tier, moduleId, "url", result.convertedUrl);
    }
  };

  const handleVideoUpload = async (tier, moduleId, file) => {
    if (!file) return;

    const storageRef = ref(storage, `onboarding_game/${tier}/${moduleId}_${Date.now()}.mp4`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Upload error:", error);
        showMessage("❌ Upload failed: " + error.message, "error");
        setUploadProgress(0);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        updateVideo(tier, moduleId, "url", downloadURL);
        showMessage("✅ Video uploaded successfully!", "success");
        setUploadProgress(0);
      }
    );
  };

  const openEditModal = (moduleId, tier) => {
    setEditModal({ open: true, moduleId, tier });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, moduleId: null, tier: null });
  };

  const getStatusBadge = (tier, moduleId) => {
    const video = videos[tier]?.[moduleId];
    if (video?.url && video?.title) {
      return <span className="status-badge active">✓ Active</span>;
    } else if (video?.url || video?.title) {
      return <span className="status-badge warning">! Incomplete</span>;
    } else {
      return <span className="status-badge empty">○ Empty</span>;
    }
  };

  if (!isAdmin) {
    return (
      <div className="video-manager-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only administrators can manage onboarding videos.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="video-manager-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="video-manager-container">
      {/* Header */}
      <div className="vm-header">
        <h1 className="vm-title">
          <span className="vm-icon">🎮</span>
          Onboarding Game Video Manager
        </h1>
        <p className="vm-subtitle">Manage videos for the interactive onboarding experience</p>
      </div>

      {/* Message Banner */}
      {message.text && (
        <div className={`vm-message vm-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="vm-tabs">
        <button
          className={`vm-tab ${activeTab === "free" ? "active" : ""}`}
          onClick={() => setActiveTab("free")}
        >
          <span className="tab-icon">🆓</span>
          Free Users
          <span className="tab-badge">{MODULES.length} modules</span>
        </button>
        <button
          className={`vm-tab ${activeTab === "paid" ? "active" : ""}`}
          onClick={() => setActiveTab("paid")}
        >
          <span className="tab-icon">💎</span>
          Paid Users
          <span className="tab-badge">{MODULES.length} modules</span>
        </button>
      </div>

      {/* Content */}
      <div className="vm-content">
        {SECTIONS.map((section) => {
          const sectionModules = getModulesBySection(section.name);
          if (sectionModules.length === 0) return null;

          const isExpanded = expandedSections[section.name];

          return (
            <div key={section.name} className="vm-section">
              <button
                className={`section-header ${isExpanded ? "expanded" : ""}`}
                onClick={() => toggleSection(section.name)}
              >
                <span className="section-icon">{section.icon}</span>
                <span className="section-name">{section.name}</span>
                <span className="section-count">{sectionModules.length} modules</span>
                <span className="section-chevron">{isExpanded ? "▼" : "▶"}</span>
              </button>

              {isExpanded && (
                <div className="section-content">
                  {sectionModules.map((module) => {
                    const videoData = videos[activeTab]?.[module.id] || {};
                    const urlValidation = validateAndConvertUrl(videoData.url || "");

                    return (
                      <div key={module.id} className="module-card">
                        <div className="module-header">
                          <div className="module-title-row">
                            <span className="module-id">{module.id.toUpperCase()}</span>
                            <span className="module-title">{module.title}</span>
                            {getStatusBadge(activeTab, module.id)}
                          </div>
                        </div>

                        <div className="module-body">
                          <div className="input-group">
                            <label className="input-label">
                              <span>Video Title</span>
                              <span className="label-hint">Display name for this module</span>
                            </label>
                            <input
                              type="text"
                              className="vm-input"
                              value={videoData.title || ""}
                              onChange={(e) => updateVideo(activeTab, module.id, "title", e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              placeholder="Enter video title..."
                            />
                          </div>

                          <div className="input-group">
                            <label className="input-label">
                              <span>Video URL</span>
                              <span className="label-hint">Firebase Storage, YouTube, or Vimeo</span>
                              {urlValidation.type !== "empty" && (
                                <span className={`url-type url-type-${urlValidation.type}`}>
                                  {urlValidation.message}
                                </span>
                              )}
                            </label>
                            <input
                              type="text"
                              className="vm-input"
                              value={videoData.url || ""}
                              onChange={(e) => updateVideo(activeTab, module.id, "url", e.target.value)}
                              onBlur={(e) => handleUrlBlur(activeTab, module.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              placeholder="https://..."
                            />
                          </div>

                          <div className="input-group">
                            <label className="input-label">
                              <span>Description</span>
                              <span className="label-hint">Optional description for this video</span>
                            </label>
                            <input
                              type="text"
                              className="vm-input"
                              value={videoData.description || ""}
                              onChange={(e) => updateVideo(activeTab, module.id, "description", e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              placeholder="Enter description..."
                            />
                          </div>

                          <div className="module-actions">
                            <label className="upload-btn">
                              📤 Upload Video
                              <input
                                type="file"
                                accept="video/*"
                                onChange={(e) => handleVideoUpload(activeTab, module.id, e.target.files[0])}
                                style={{ display: "none" }}
                              />
                            </label>
                            {videoData.url && (
                              <a
                                href={videoData.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="preview-btn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                👁️ Preview
                              </a>
                            )}
                          </div>

                          {uploadProgress > 0 && (
                            <div className="upload-progress">
                              <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                              </div>
                              <span className="progress-text">{Math.round(uploadProgress)}%</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      <div className="vm-footer">
        <button
          className="save-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="spinner-small"></span>
              Saving...
            </>
          ) : (
            <>
              💾 Save All Changes
            </>
          )}
        </button>
        <p className="save-note">
          Changes will take effect immediately for all users
        </p>
      </div>
    </div>
  );
}
